/**
 * TCB R38 replenishment / reminder / PM runtime (full pack).
 * Extends R36 reorder + R14 maintenance. Prefers reminders over subscriptions.
 * Pauses when market/tax/availability/consent gates fail. Never silently reroutes/substitutes.
 */
(function (global) {
  'use strict';

  var CFG = global.TCB_REPLENISHMENT_INTELLIGENCE || null;
  var MAX_SKUS = 8;

  function loadConfig(cb) {
    if (!CFG && global.TCB_REPLENISHMENT_INTELLIGENCE) CFG = global.TCB_REPLENISHMENT_INTELLIGENCE;
    if (CFG) {
      cb(null, CFG);
      return;
    }
    var node = document.querySelector('[data-tcb-replenishment-runtime-url]');
    var url = node && node.getAttribute('data-tcb-replenishment-runtime-url');
    if (!url || !global.fetch) {
      cb(new Error('replenishment_config_missing'));
      return;
    }
    global
      .fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        CFG = json;
        global.TCB_REPLENISHMENT_INTELLIGENCE = json;
        cb(null, json);
      })
      .catch(function (err) {
        cb(err || new Error('replenishment_fetch_failed'));
      });
  }

  function getPlan(planId) {
    return (
      ((CFG && CFG.plans) || []).find(function (p) {
        return p.plan_id === planId;
      }) || null
    );
  }

  function listPlans(filter) {
    filter = filter || {};
    return ((CFG && CFG.plans) || []).filter(function (p) {
      if (filter.company_id && p.company_id !== filter.company_id) return false;
      if (filter.location_id && p.location_id !== filter.location_id && p.company_location_id !== filter.location_id)
        return false;
      if (filter.status && p.status !== filter.status && p.state !== filter.status) return false;
      return true;
    });
  }

  function isSubscriptionType(planType) {
    return planType === 'SUBSCRIPTION_PAY_PER_DELIVERY' || planType === 'PREPAID_SUBSCRIPTION';
  }

  function subscriptionAllowed() {
    return !!(CFG && CFG.feature_flags && CFG.feature_flags.r38_subscription_adapter === true);
  }

  function evaluateEligibility(plan) {
    var reasons = [];
    if (!plan.consent_at && plan.consent_required !== false) reasons.push('consent_missing');
    (plan.lines || []).forEach(function (line) {
      if ((line.unique_sku_count || 0) > MAX_SKUS) reasons.push('sku_cap_exceeded');
      if (line.eligibility === 'discontinued' || line.eligibility === 'incompatible') {
        reasons.push('line_ineligible:' + (line.line_id || line.product_variant_id));
      }
    });
    if (isSubscriptionType(plan.plan_type) && !subscriptionAllowed()) {
      reasons.push('subscription_adapter_disabled');
    }
    return reasons;
  }

  function evaluateCycle(planId, context) {
    context = context || {};
    var plan = getPlan(planId);
    if (!plan) return { ok: false, status: 'REVIEW_REQUIRED', reasons: ['unknown_plan'] };

    var reasons = evaluateEligibility(plan);
    var status = plan.status === 'PAUSED' || plan.state === 'PAUSED' ? 'PAUSED' : 'READY';

    if (plan.status === 'PAUSED' || plan.state === 'PAUSED') {
      reasons.push(plan.pause_reason || 'plan_paused');
    }
    if (plan.status === 'BLOCKED' || plan.state === 'BLOCKED') {
      status = 'BLOCKED';
      reasons.push('plan_blocked');
    }

    if (reasons.indexOf('sku_cap_exceeded') !== -1 || reasons.indexOf('consent_missing') !== -1) {
      status = 'REVIEW_REQUIRED';
    }
    if (reasons.indexOf('subscription_adapter_disabled') !== -1) {
      status = 'BLOCKED';
    }

    // R37 tax readiness if runtime present
    if (global.TCBTaxDocumentRuntime && typeof global.TCBTaxDocumentRuntime.evaluateLocationTax === 'function') {
      var tax = global.TCBTaxDocumentRuntime.evaluateLocationTax(plan.company_id, plan.location_id || plan.company_location_id);
      if (tax && tax.state && tax.state !== 'EXEMPT_VERIFIED' && tax.state !== 'TAXABLE' && tax.state !== 'NOT_APPLICABLE') {
        if (tax.state === 'EXEMPT_EXPIRED' || tax.state === 'EXEMPT_PENDING_REVIEW' || tax.state === 'UNKNOWN') {
          status = 'PAUSED';
          reasons.push('r37_tax_not_ready:' + tax.state);
        }
      }
    }

    // R39 market eligibility if runtime present
    if (global.TCBMarketsRuntime && typeof global.TCBMarketsRuntime.isReplenishmentAllowed === 'function') {
      var marketOk = global.TCBMarketsRuntime.isReplenishmentAllowed(plan.market_key || context.market_key, {
        company_id: plan.company_id,
        location_id: plan.location_id || plan.company_location_id
      });
      if (!marketOk || marketOk.ok === false) {
        status = 'PAUSED';
        reasons.push('market_replenishment_blocked:' + ((marketOk && marketOk.reason) || 'ineligible'));
      }
    }

    // Never silently reroute or substitute
    if (context.force_reroute) {
      status = 'REVIEW_REQUIRED';
      reasons.push('silent_reroute_forbidden');
    }
    if (context.silent_substitute) {
      status = 'REVIEW_REQUIRED';
      reasons.push('silent_substitution_forbidden');
    }

    // Cadence honesty: unknown source cannot claim OEM-required
    if (plan.cadence_source === 'unknown' && context.claim_oem_required) {
      status = 'REVIEW_REQUIRED';
      reasons.push('false_oem_required_claim');
    }

    return {
      ok: status === 'READY',
      status: status,
      plan_id: planId,
      plan_type: plan.plan_type,
      company_id: plan.company_id,
      location_id: plan.location_id || plan.company_location_id,
      market_key: plan.market_key || null,
      reasons: reasons,
      revalidation: plan.revalidation_required || [],
      pause_policy: plan.pause_policy,
      one_click_reorder: plan.one_click_reorder === true || plan.plan_type === 'MANUAL_REORDER',
      auto_ship: plan.auto_ship === true && subscriptionAllowed(),
      engine: 'tcb-replenishment-runtime',
      version: (CFG && CFG.version) || 'r38'
    };
  }

  function applyControl(planId, control, payload) {
    payload = payload || {};
    var plan = getPlan(planId);
    if (!plan) return { ok: false, reason: 'unknown_plan' };
    var allowed = plan.customer_controls || (CFG && CFG.customer_controls) || [];
    if (allowed.indexOf(control) === -1) return { ok: false, reason: 'control_not_allowed' };

    var nextStatus = plan.status;
    if (control === 'pause') nextStatus = 'PAUSED';
    if (control === 'skip_next') nextStatus = 'SKIPPED_NEXT';
    if (control === 'cancel') nextStatus = 'CANCELED';
    if (control === 'change_quantity' && (!payload.quantity || payload.quantity < 1)) {
      return { ok: false, reason: 'invalid_quantity' };
    }
    if (control === 'substitute_review') {
      return { ok: true, status: 'REVIEW_REQUIRED', control: control, requires_staff: true };
    }

    track('replenishment_' + control, { plan_id: planId, payload: payload });
    return { ok: true, status: nextStatus, control: control, dry_run: true };
  }

  function queueReminders(filter) {
    var queue = (CFG && CFG.reminder_queue) || [];
    if (!filter) return queue.slice();
    return queue.filter(function (r) {
      if (filter.location_id && r.location_id !== filter.location_id) return false;
      if (filter.company_id && r.company_id !== filter.company_id) return false;
      return true;
    });
  }

  function oneClickReorder(planId) {
    var evaluation = evaluateCycle(planId, {});
    if (!evaluation.ok && evaluation.status !== 'READY') {
      return { ok: false, status: evaluation.status, reasons: evaluation.reasons };
    }
    track('one_click_reorder_started', { plan_id: planId });
    return {
      ok: true,
      status: 'REORDER_DRAFT',
      plan_id: planId,
      reserves_inventory: false,
      live_write: false,
      note: 'Creates reorder draft via R36 template path; no live subscription write'
    };
  }

  function track(eventName, payload) {
    var detail = Object.assign({ event: eventName, at: new Date().toISOString() }, payload || {});
    try {
      if (global.TCBAnalytics && typeof global.TCBAnalytics.track === 'function') global.TCBAnalytics.track(eventName, detail);
      global.dispatchEvent(new CustomEvent('tcb:replenishment', { detail: detail }));
    } catch (e) {
      /* no-op */
    }
  }

  global.TCBReplenishmentRuntime = {
    version: 'r38.1.0',
    maxSkus: MAX_SKUS,
    loadConfig: loadConfig,
    getPlan: getPlan,
    listPlans: listPlans,
    evaluateCycle: evaluateCycle,
    evaluateEligibility: evaluateEligibility,
    applyControl: applyControl,
    queueReminders: queueReminders,
    oneClickReorder: oneClickReorder,
    subscriptionAllowed: subscriptionAllowed,
    track: track
  };
})(typeof window !== 'undefined' ? window : globalThis);
